import React, { useState, useEffect } from 'react';
import { getApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Timestamp } from './ui/timestamp';

// Interface for Celery task stats
interface TaskStats {
  active: number;
  scheduled: number;
  reserved: number;
  queued: number;
}

// Interface for worker stats
interface WorkerStats {
  registered: string[];
  count: number;
}

// Interface for queue lengths
interface QueueLengths {
  [queueName: string]: number;
}

// Interface for task monitoring API response
interface TaskMonitoringResponse {
  workers: WorkerStats;
  tasks: TaskStats;
  queues: QueueLengths;
  active_tasks_by_worker: {
    [workerName: string]: {
      id: string;
      name: string;
      time_start: number;
      ack: boolean;
    }[];
  };
  error?: string;
}

const AdminTaskMonitor: React.FC = () => {
  const [taskStats, setTaskStats] = useState<TaskMonitoringResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number>(10); // seconds
  const api = getApi();
  const { user } = useAuth();

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase?.() === 'admin';

  // Fetch task stats from API
  const fetchTaskStats = async () => {
    if (!isAdmin) {
      setError('Admin access required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/v1/admin/tasks');
      setTaskStats(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching task stats:', err);
      setError(err.message || 'Failed to fetch task statistics');
    } finally {
      setLoading(false);
    }
  };

  // Set up interval for refreshing task stats
  useEffect(() => {
    fetchTaskStats();

    const intervalId = setInterval(() => {
      fetchTaskStats();
    }, refreshInterval * 1000);

    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 p-5 rounded">
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p>You need administrator privileges to access the task monitor.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Celery Task Monitor</h2>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Refresh every</label>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
            className="border rounded p-1 text-sm"
          >
            <option value="5">5s</option>
            <option value="10">10s</option>
            <option value="30">30s</option>
            <option value="60">60s</option>
          </select>
          <button
            onClick={fetchTaskStats}
            className="ml-2 bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
          >
            Refresh Now
          </button>
        </div>
      </div>

      {loading && !taskStats && (
        <div className="py-10 text-center text-gray-500">Loading task information...</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded mb-4">
          <p className="font-medium">Error loading task data</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {taskStats && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-blue-800 mb-1">Active Tasks</h3>
              <p className="text-2xl font-bold text-blue-900">{taskStats.tasks.active}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-purple-800 mb-1">Scheduled Tasks</h3>
              <p className="text-2xl font-bold text-purple-900">{taskStats.tasks.scheduled}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-green-800 mb-1">Reserved Tasks</h3>
              <p className="text-2xl font-bold text-green-900">{taskStats.tasks.reserved}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-yellow-800 mb-1">Queued Tasks</h3>
              <p className="text-2xl font-bold text-yellow-900">{taskStats.tasks.queued}</p>
            </div>
          </div>

          {/* Worker Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Workers ({taskStats.workers.count})</h3>
            <div className="flex flex-wrap gap-2">
              {taskStats.workers.registered.map((worker, index) => (
                <span
                  key={index}
                  className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded"
                >
                  {worker}
                </span>
              ))}
              {taskStats.workers.registered.length === 0 && (
                <span className="text-gray-500 text-sm">No active workers found</span>
              )}
            </div>
          </div>

          {/* Queue Lengths */}
          <div>
            <h3 className="font-medium mb-2">Queue Lengths</h3>
            <div className="bg-white border rounded overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Queue Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Length
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(taskStats.queues).map(([queueName, length], index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {queueName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {length}
                      </td>
                    </tr>
                  ))}
                  {Object.keys(taskStats.queues).length === 0 && (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500">
                        No queues found or Redis connection unavailable
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Active Tasks By Worker */}
          <div>
            <h3 className="font-medium mb-2">Active Tasks</h3>
            {Object.entries(taskStats.active_tasks_by_worker).length > 0 ? (
              Object.entries(taskStats.active_tasks_by_worker).map(([workerName, tasks]) => (
                <div key={workerName} className="mb-4">
                  <h4 className="text-sm font-medium mb-2">
                    {workerName} ({tasks.length} tasks)
                  </h4>
                  {tasks.length > 0 ? (
                    <div className="bg-white border rounded overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Task ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Started
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tasks.map((task, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                {task.id?.substring(0, 8)}...
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {task.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {task.time_start ? <Timestamp value={new Date(task.time_start * 1000)} format="absolute" /> : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {task.ack ? (
                                  <span className="text-green-600">Acknowledged</span>
                                ) : (
                                  <span className="text-yellow-600">Pending</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 pl-4">No active tasks</p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No active tasks found</p>
            )}
          </div>

          {/* Link to Flower dashboard if available */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              For more detailed monitoring, consider using the Flower dashboard:
            </p>
            <a
              href="http://localhost:5555"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Open Flower Dashboard
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTaskMonitor;